interface LabelPlayersAction {
  initial: boolean;
  desired: boolean;
  label: Label;
}

class RemoveDepartmentLabelsDropdownController {
  private players: LabelPlayer[];
  private availableLabels: Label[];
  private selectedLabel: Label;
  private onChange: (args: { $changes: LabelPlayersChange[] }) => void;

  private actions: LabelPlayersAction[];

  constructor(private $uibModal) {}

  createAvailableActions() {
    if (!this.players.length || !this.availableLabels.length || !this.selectedLabel) {
      return (this.actions = null);
    }

    const availableLabelsForRemove = this.availableLabels.filter(
      (label) => label._id === this.selectedLabel._id,
    );

    this.actions = availableLabelsForRemove.map((label) => {
      return { initial: true, desired: true, label: label };
    });
  }

  changeAction(event: MouseEvent, action: LabelPlayersAction) {
    switch (action.desired) {
      case true:
        action.desired = false;
        break;

      case false:
        action.desired = true;
        break;
    }

    event.preventDefault();
  }

  get isApplyPossible() {
    return (this.actions || []).some((item) => item.desired !== item.initial);
  }

  async apply() {
    let confirmationText;
    let modalTitle = 'Confirm Removal';

    if (!this.selectedLabel) {
      modalTitle = 'Remove players from Scouting Department';
      const playerCount = this.players.length;

      const playersWithLists = this.players.filter(
        (player) => player.labels.length > 1 || player.labels.some((label) => label.label),
      );

      const playersToRemove = this.players.filter(
        (player) => !(player.labels.length > 1 || player.labels.some((label) => label.label)),
      );

      const hasPlayersToRemove = playersToRemove.length > 0;

      if (playersWithLists.length > 0) {
        const playersToRemoveHtml =
          playersToRemove.length > 0
            ? playersToRemove
                .map((player) => `<li>${player.player.firstName} ${player.player.lastName}</li>`)
                .join('')
            : '';

        confirmationText = `
          <div>
            
            
            ${
              playersToRemove.length > 0
                ? `
              <strong>${playersToRemove.length} player(s) will be removed from the Scouting Department:</strong>
              <div class="modal-scrollable-list" style="margin-bottom: 10px;">
                <ul style="margin-bottom: 0;">
                  ${playersToRemoveHtml}
                </ul>
              </div>
              <strong class="bold">Important: ${playersWithLists.length} of ${playerCount} selected player(s) can't be removed because they are assigned to a list.</strong>
            `
                : `<strong class="text-danger">All selected players are still assigned to a list.
                Remove them from the list before removing them from the Scouting Department.</strong>`
            }
          </div>
        `;
      } else {
        // All selected players that have no lists and will be removed
        const playersToRemoveHtml = playersToRemove
          .map((player) => `<li>${player.player.firstName} ${player.player.lastName}</li>`)
          .join('');

        confirmationText = `
          <div>
            <strong>${playerCount} player(s) will be removed from the Scouting Department:</strong>
            <div class="modal-scrollable-list">
              <ul style="margin-bottom: 0;">
                ${playersToRemoveHtml}
              </ul>
            </div>
          </div>
        `;
      }

      const modalInstance = this.$uibModal.open({
        size: 'md',
        resolve: {
          noPlayersToRemove: () => !hasPlayersToRemove,
        },
        template: `
          <div class="sc-modal">
            <div class="modal-header">
              <h3 class="modal-title">${modalTitle}</h3>
            </div>
            <div class="modal-body md-body-1">
              ${confirmationText}
            </div>
            <div class="modal-footer">
              <div layout layout-align="space-between center" class="m-n">
                <md-button class="md-raised" ng-click="$ctrl.cancel()">
                  CANCEL
                </md-button>
                <md-button 
                  class="md-primary md-raised" 
                  ng-click="$ctrl.confirm()"
                  ng-disabled="$ctrl.noPlayersToRemove"
                  title="{{$ctrl.noPlayersToRemove ? 'All selected players belong to lists. Nothing to remove with this action.' : ''}}"
                >
                  REMOVE
                </md-button>
              </div>
            </div>
          </div>
        `,
        controller: [
          '$uibModalInstance',
          'noPlayersToRemove',
          function ($uibModalInstance, noPlayersToRemove) {
            this.modalInstance = $uibModalInstance;
            this.noPlayersToRemove = noPlayersToRemove;

            this.cancel = function () {
              $uibModalInstance.dismiss('cancel');
            };

            this.confirm = function () {
              $uibModalInstance.close('confirm');
            };
          },
        ],
        controllerAs: '$ctrl',
      });

      try {
        const result = await modalInstance.result;

        if (result === 'confirm') {
          // Only create removal operations for players without custom lists
          const changes = playersToRemove.flatMap((player) => {
            return player.labels.map((label) => {
              return { type: 'remove' as const, player: player, label: label._id };
            });
          });

          this.onChange({ $changes: changes });
        }
      } catch (_e) {
        // First modal was dismissed
      }
    } else {
      const labelName = this.selectedLabel.label.name;
      const playerCount = this.players.length;

      modalTitle = 'Remove from List';
      confirmationText = `
        <div>
          <strong>${playerCount} player(s) will be removed from the "${labelName}" list:</strong>
          <div class="modal-scrollable-list">
            <ul style="margin-bottom: 0;">
              ${this.players
                .map((player) => `<li>${player.player.firstName} ${player.player.lastName}</li>`)
                .join('')}
            </ul>
          </div>
        </div>
      `;

      const modalInstance = this.$uibModal.open({
        size: 'md',
        template: `
          <div class="sc-modal">
            <div class="modal-header">
              <h3 class="modal-title">${modalTitle}</h3>
            </div>
            <div class="modal-body md-body-1">
              ${confirmationText}
            </div>
            <div class="modal-footer">
              <div layout layout-align="space-between center" class="m-n">
                <md-button class="md-raised" ng-click="$ctrl.cancel()">
                  CANCEL
                </md-button>
                <md-button class="md-primary md-raised" ng-click="$ctrl.confirm()">
                  REMOVE
                </md-button>
              </div>
            </div>
          </div>
        `,
        controller: [
          '$uibModalInstance',
          function ($uibModalInstance) {
            this.modalInstance = $uibModalInstance;

            this.cancel = function () {
              $uibModalInstance.dismiss('cancel');
            };

            this.confirm = function () {
              $uibModalInstance.close('confirm');
            };
          },
        ],
        controllerAs: '$ctrl',
      });

      try {
        const result = await modalInstance.result;

        if (result === 'confirm') {
          const changes = this.players.map((player) => {
            return {
              type: 'remove' as const,
              player: player,
              label: this.selectedLabel._id || null,
            };
          });

          this.onChange({ $changes: changes });
        }
      } catch (_e) {
        // Do nothing
      }
    }
  }
}

angular.module('app.scouting').component('scoutingRemoveDepartmentLabelsDropdown', {
  templateUrl: 'scouting/components/remove-department-labels-dropdown.html',
  controller: RemoveDepartmentLabelsDropdownController,
  bindings: { players: '<', availableLabels: '<', selectedLabel: '<', onChange: '&' },
});
